import React, { useEffect, useState } from "react"
import { Container, Row, Col, Card, CardBody, Table, Button } from "reactstrap"
import _ from "lodash"

import * as backend from "../../helpers/backend_helper"
import Toaster from "../../components/Common/Toaster"

// The six report types a submission can be scored against.
const REPORTS = [
  { key: "dailymonitoring", label: "Daily Monitoring" },
  { key: "dailydocument", label: "Daily Document Review" },
  { key: "dailywork", label: "Daily Work Report" },
  { key: "monthlyonsite", label: "Monthly Monitoring" },
  { key: "monthlydocument", label: "Monthly Document Review" },
  { key: "monthlywork", label: "Monthly Work Report" },
]

// Factory config: assign which reports each individual is responsible for and how
// many performance points a submission earns. Feeds the Performance Score page.
const ReportScoring = (props: any) => {
  const { factory } = props
  const orgId = factory && factory.orgId
  const facId = factory && factory._id

  const [userList, setUserList] = useState<any[]>([])
  // scores[userId][reportKey] = points (string in the input, number when saved)
  const [scores, setScores] = useState<{ [uid: string]: { [k: string]: any } }>({})
  // shared[userId][reportKey] = true → one submission by anyone credits the team
  const [shared, setShared] = useState<{ [uid: string]: { [k: string]: boolean } }>({})

  useEffect(() => {
    backend.getUserOrg(orgId).then(setUserList).catch(() => {})
  }, [orgId])

  useEffect(() => {
    const initial: any = {}
    const initShared: any = {}
    ;((factory && factory.reportScoring) || []).forEach((entry: any) => {
      initial[entry.userId] = {}
      initShared[entry.userId] = {}
      ;(entry.items || []).forEach((it: any) => {
        initial[entry.userId][it.report] = it.score
        initShared[entry.userId][it.report] = !!it.shared
      })
    })
    setScores(initial)
    setShared(initShared)
    // Seed only when the factory actually changes — not on every parent re-render,
    // which would wipe the user's edits (e.g. reset a Shared checkbox right after a click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factory && factory._id])

  const teamUsers = (factory && factory.teams ? factory.teams : [])
    .filter((t: any) => t.status !== false) // hide inactive staff
    .map((t: any) => _.find(userList, ["_id", t.userId]))
    .filter(Boolean)

  const setScore = (uid: string, key: string, val: string) => {
    setScores((s) => ({ ...s, [uid]: { ...(s[uid] || {}), [key]: val } }))
  }
  const setSharedFlag = (uid: string, key: string, val: boolean) => {
    setShared((s) => ({ ...s, [uid]: { ...(s[uid] || {}), [key]: val } }))
  }

  const submit = async () => {
    const reportScoring = Object.keys(scores)
      .map((uid) => ({
        userId: uid,
        items: REPORTS.map((r) => ({
          report: r.key,
          score: Number(scores[uid][r.key]) || 0,
          shared: !!(shared[uid] && shared[uid][r.key]),
        }))
          .filter((it) => it.score > 0),
      }))
      .filter((e) => e.items.length > 0)
    try {
      await backend.updateFactory(orgId, facId, { reportScoring })
      props.update && props.update()
      Toaster.success("Report scoring saved")
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to save report scoring")
    }
  }

  return (
    <Container fluid>
      <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.7 }}>
        <p className="mb-1">
          Mark <strong>Yes</strong> on each report a person is responsible for, or <strong>No</strong> if it&apos;s
          not theirs. On the <strong>Performance Score</strong> page, over the chosen date range, each person is
          scored on the reports marked <strong>Yes</strong> — based on how often they actually submitted them.
        </p>
        <p className="mb-0">
          Turn on <strong>Shared</strong> for a cell when a report is a <strong>team responsibility</strong>: then a
          submission by <em>anyone</em> assigned that report credits everyone. Leave it off to score each person
          only on their own submissions.
        </p>
      </div>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              {teamUsers.length === 0 ? (
                <div className="mw-empty-state">Add team members first (Teams tab).</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table mw-table table-bordered mb-0" style={{ textAlign: "center" }}>
                    <thead>
                      <tr className="table-success">
                        <th style={{ textAlign: "left" }}>Individual</th>
                        {REPORTS.map((r) => (
                          <th key={r.key}>{r.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {teamUsers.map((u: any) => (
                        <tr key={u._id}>
                          <th scope="row" style={{ textAlign: "left" }}>
                            {u.name} <small className="text-muted">({u.role})</small>
                          </th>
                          {REPORTS.map((r) => {
                            const val = (scores[u._id] && scores[u._id][r.key]) || ""
                            const isYes = Number(val) > 0
                            const isShared = !!(shared[u._id] && shared[u._id][r.key])
                            return (
                              <td key={r.key} style={{ minWidth: 110 }}>
                                <button
                                  type="button"
                                  title="Yes: this report is assigned to this person and counts towards their score"
                                  onClick={() => setScore(u._id, r.key, isYes ? "0" : "1")}
                                  style={{
                                    border: `1px solid ${isYes ? "#2ea99b" : "#cdd5dd"}`,
                                    borderRadius: 8,
                                    padding: "5px 20px",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    background: isYes ? "#2ea99b" : "#fff",
                                    color: isYes ? "#fff" : "#6b7785",
                                    minWidth: 70,
                                  }}
                                >
                                  {isYes ? "Yes" : "No"}
                                </button>
                                <div style={{ textAlign: "center", marginTop: 5 }}>
                                  <button
                                    type="button"
                                    title="Shared: one submission by anyone credits the whole team"
                                    onClick={() => setSharedFlag(u._id, r.key, !isShared)}
                                    style={{
                                      border: `1px solid ${isShared ? "#2ea99b" : "#cdd5dd"}`,
                                      borderRadius: 12,
                                      padding: "1px 10px",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      background: isShared ? "#2ea99b" : "#fff",
                                      color: isShared ? "#fff" : "#6b7785",
                                    }}
                                  >
                                    {isShared ? "✓ Shared" : "Shared"}
                                  </button>
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              {teamUsers.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                  <Button color="success" onClick={submit}>Save</Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default ReportScoring
